type PollingMessage = {
  type: string;
  payload?: {
    contacts?: any[];
    unreadStatus?: Record<string, boolean>;
    contactID?: number;
    isSent?: boolean;
    [key: string]: any;
  };
  data?: any;
  timestamp?: number;
  sectorId?: number;
  status?: string;
  [key: string]: any;
};

type OnMessageCallback = (msg: any) => void;
type OnConnectionStatusChange = (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;

// Intervalo de polling em milissegundos
const POLLING_INTERVAL = 1000;

// Chave global para o singleton
const GLOBAL_POLLING_KEY = '__GLOBAL_CHAT_POLLING_INSTANCE__';

class ChatWebSocketService {
  private onMessageCallback: OnMessageCallback | null = null;
  private onConnectionStatusChange: OnConnectionStatusChange | null = null;
  private contactsListHandlers: ((contacts: any[]) => void)[] = [];
  private contactHandlers: ((contact: any) => void)[] = [];
  private messageHandlers: ((msg: any) => void)[] = [];
  private unreadTotalHandlers: ((payload: any) => void)[] = [];
  private currentSectorId: number | null = null;
  private currentToken: string | null = null;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private lastEventId: number = 0;
  private consecutiveErrors: number = 0;
  private maxConsecutiveErrors: number = 10;
  private isIntentionalDisconnect: boolean = false;
  private isPolling: boolean = false;
  private lastPollTime: number = 0;

  private _isConnected: boolean = false;
  private _connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected';

  constructor() {
    if ((window as any)[GLOBAL_POLLING_KEY]) {
      return (window as any)[GLOBAL_POLLING_KEY];
    }
    (window as any)[GLOBAL_POLLING_KEY] = this;

    this.setupVisibilityListener();
  }

  private setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (this._isConnected && this.currentSectorId && this.currentToken) {
          this.pollNow();
        } else if (!this._isConnected && this.currentSectorId && this.currentToken && !this.isIntentionalDisconnect) {
          this.startPolling();
        }
      }
    });
  }

  public get isConnected(): boolean {
    return this._isConnected;
  }

  public get connectionStatus(): 'connecting' | 'connected' | 'disconnected' | 'error' {
    return this._connectionStatus;
  }

  public isConnectedToSector(sectorId: number): boolean {
    return this._isConnected && this.currentSectorId === sectorId;
  }

  public hasActiveConnection(): boolean {
    return this._isConnected && this.pollingTimer !== null;
  }

  public getCurrentSectorId(): number | null {
    return this.currentSectorId;
  }

  public needsReconnect(newSectorId: number): boolean {
    return !this._isConnected || this.currentSectorId !== newSectorId;
  }

  public isHealthy(): boolean {
    if (!this._isConnected) return false;
    const timeSinceLastPoll = Date.now() - this.lastPollTime;
    return timeSinceLastPoll < POLLING_INTERVAL * 3;
  }

  private setConnectionStatus(status: 'connecting' | 'connected' | 'disconnected' | 'error') {
    if (this._connectionStatus !== status) {
      this._connectionStatus = status;
      this._isConnected = status === 'connected';

      if (this.onConnectionStatusChange) {
        this.onConnectionStatusChange(status);
      }

      window.dispatchEvent(new CustomEvent('websocketStatusChange', {
        detail: { status, sectorId: this.currentSectorId }
      }));
    }
  }

  private getPollingBaseUrl(): string {
    return process.env.REACT_APP_WEBSOCKET_URL || (window as any)._env_?.REACT_APP_WEBSOCKET_URL || 'https://whatsapp.ligchat.com';
  }

  private async pollNow() {
    if (this.isPolling || !this.currentSectorId || !this.currentToken) return;

    this.isPolling = true;

    try {
      const baseUrl = this.getPollingBaseUrl();
      const url = `${baseUrl}/api/v1/events/poll?sector_id=${this.currentSectorId}&since_id=${this.lastEventId}&token=${encodeURIComponent(this.currentToken)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.status === 401) {
        this.setConnectionStatus('error');
        this.stopPolling();
        this.isPolling = false;
        return;
      }

      if (response.status === 429) {
        console.warn('[ChatPollingService] Rate limited, backing off...');
        this.consecutiveErrors++;
        this.isPolling = false;
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || result;

      this.lastPollTime = Date.now();
      this.consecutiveErrors = 0;

      if (!this._isConnected) {
        this.setConnectionStatus('connected');
      }

      if (data.lastEventId) {
        this.lastEventId = data.lastEventId;
      }

      const events = data.events || [];
      for (const event of events) {
        this.processEvent(event);
      }

    } catch (error) {
      this.consecutiveErrors++;

      if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
        this.setConnectionStatus('error');
      }
    } finally {
      this.isPolling = false;
    }
  }

  private processEvent(event: any) {
    const data: PollingMessage = {
      type: event.type,
      payload: event.payload,
      sectorId: event.sectorId,
      timestamp: event.timestamp,
    };

    switch (data.type) {
      case 'message':
        this.handleNewMessage(data);
        break;
      case 'contact':
        this.handleContactUpdate(data);
        break;
      case 'contacts_list':
        this.handleContactsList(data);
        break;
      case 'unread_status':
        this.handleUnreadStatus(data);
        break;
      case 'unread_total':
        this.handleUnreadTotal(data);
        break;
      case 'whatsapp_status': {
        const statusPayload = data.payload || {};
        if (statusPayload.status === 'connected') {
          window.dispatchEvent(new CustomEvent('whatsappConnected', {
            detail: { sectorId: statusPayload.sectorId, message: statusPayload.message }
          }));
        } else if (statusPayload.status === 'disconnected' || statusPayload.status === 'error') {
          window.dispatchEvent(new CustomEvent('whatsappDisconnected', {
            detail: { sectorId: statusPayload.sectorId, message: statusPayload.message }
          }));
        }
        window.dispatchEvent(new CustomEvent('whatsappStatusChange', {
          detail: statusPayload
        }));
        break;
      }
      case 'groups_updated':
        window.dispatchEvent(new CustomEvent('groupsUpdated', {
          detail: { sectorId: (data.payload as any)?.sectorId ?? data.sectorId }
        }));
        break;
    }

    if (this.onMessageCallback) {
      try {
        this.onMessageCallback(data);
      } catch (error) {
        console.error('[ChatPollingService] Error in onMessageCallback:', error);
      }
    }
  }

  private startPolling() {
    this.stopPolling();
    this.setConnectionStatus('connecting');

    this.pollNow().then(() => {
      if (!this.isIntentionalDisconnect) {
        this.pollingTimer = setInterval(() => {
          this.pollNow();
        }, POLLING_INTERVAL);
      }
    });
  }

  private stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  async connect(token: string, onMessage: OnMessageCallback, sectorId?: number, onStatusChange?: OnConnectionStatusChange): Promise<void> {
    if (this._isConnected && this.currentSectorId === sectorId) {
      this.onMessageCallback = onMessage;
      if (onStatusChange) this.onConnectionStatusChange = onStatusChange;
      return;
    }

    if (this._isConnected && this.currentSectorId !== sectorId) {
      this.stopPolling();
    }

    this.isIntentionalDisconnect = false;
    this.consecutiveErrors = 0;
    this.currentToken = token;
    this.currentSectorId = sectorId || null;
    this.onMessageCallback = onMessage;
    if (onStatusChange) this.onConnectionStatusChange = onStatusChange;
    this.lastEventId = 0;

    this.startPolling();
  }

  private handleNewMessage(data: PollingMessage): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('[ChatPollingService] Error in message handler:', error);
      }
    });
  }

  private handleContactUpdate(data: PollingMessage): void {
    this.contactHandlers.forEach(handler => {
      try {
        handler(data.payload);
      } catch (error) {
        console.error('[ChatPollingService] Error in contact handler:', error);
      }
    });
  }

  private handleContactsList(data: PollingMessage): void {
    this.contactsListHandlers.forEach(handler => {
      try {
        handler(data.payload?.contacts || []);
      } catch (error) {
      }
    });
  }

  private handleUnreadStatus(data: PollingMessage): void {
  }

  private handleUnreadTotal(data: PollingMessage): void {
    this.unreadTotalHandlers.forEach(handler => {
      try {
        handler(data.payload);
      } catch (error) {
        console.error('[ChatPollingService] Error in unread total handler:', error);
      }
    });
  }

  addContactsListHandler(handler: (contacts: any[]) => void) {
    this.contactsListHandlers.push(handler);
  }

  removeContactsListHandler(handler: (contacts: any[]) => void) {
    this.contactsListHandlers = this.contactsListHandlers.filter(h => h !== handler);
  }

  addContactHandler(handler: (contact: any) => void) {
    this.contactHandlers.push(handler);
  }

  removeContactHandler(handler: (contact: any) => void) {
    this.contactHandlers = this.contactHandlers.filter(h => h !== handler);
  }

  addMessageHandler(handler: (msg: any) => void) {
    this.messageHandlers.push(handler);
  }

  removeMessageHandler(handler: (msg: any) => void) {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }

  addUnreadTotalHandler(handler: (payload: any) => void) {
    this.unreadTotalHandlers.push(handler);
  }

  removeUnreadTotalHandler(handler: (payload: any) => void) {
    this.unreadTotalHandlers = this.unreadTotalHandlers.filter(h => h !== handler);
  }

  disconnect() {
    this.isIntentionalDisconnect = true;
    this.stopPolling();

    this.setConnectionStatus('disconnected');
    this.currentSectorId = null;
    this.currentToken = null;
    this.onMessageCallback = null;
    this.onConnectionStatusChange = null;
    this.consecutiveErrors = 0;
    this.lastEventId = 0;
    this.lastPollTime = 0;
  }

  public forceReconnect() {
    if (this.currentSectorId && this.onMessageCallback && this.currentToken) {
      const token = this.currentToken;
      const callback = this.onMessageCallback;
      const sectorId = this.currentSectorId;
      const statusChange = this.onConnectionStatusChange;
      this.disconnect();
      setTimeout(() => {
        this.connect(token, callback, sectorId, statusChange || undefined);
      }, 1000);
    }
  }

  public checkHealthAndReconnect() {
    if (!this.isHealthy() && !this.isIntentionalDisconnect) {
      this.forceReconnect();
    }
  }

  public clearStuckConnection() {
    if (!this._isConnected && this.currentSectorId && this.currentToken && this.onMessageCallback) {
      this.startPolling();
    }
  }
}

// Exportar instância singleton
const chatWebSocketService = new ChatWebSocketService();
export default chatWebSocketService;